import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { motion } from 'framer-motion';
import { Column, Row, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel } from '~/models';
import { COBStep, getStepName, stepsTextsPath } from '../../../ClientOnboarding.common.utils';
import { getReachableInnerSteps } from '../../ClientOnboardingV2.utils';
import styles from './Sidebar.module.scss';
import { INDICATOR_HEIGHT, StepState, calculateIndicatorPosition, getStepState } from './Sidebar.utils';
export const GetSidebarTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export const Sidebar = (param)=>{
    let { testId, currentStep, onStepClick, relationship, visitedSteps } = param;
    const { SidebarTestIds } = useMemo(()=>GetSidebarTID(testId), [
        testId
    ]);
    const [indicatorStyle, setIndicatorStyle] = useState({
        top: 0,
        height: 0
    });
    const stepRefs = useRef([]);
    const { CUSTOM_PAGES } = useFeatureFlags();
    const { t: stepsTexts } = useLocaleKeys(stepsTextsPath);
    const reachableSteps = useMemo(()=>getReachableInnerSteps(relationship, {
            customPagesFFEnabled: !!CUSTOM_PAGES
        }).filter((step)=>step !== COBStep.allSet), [
        relationship,
        CUSTOM_PAGES
    ]);
    const vendorLogoUrl = relationshipModel.vendorLogoUrl({
        vendor: relationship.vendor
    });
    const currentStepIndex = reachableSteps.indexOf(currentStep);
    useLayoutEffect(()=>{
        const currentRef = stepRefs.current[currentStepIndex];
        if (currentRef) {
            var _currentRef_scrollIntoView;
            setIndicatorStyle(calculateIndicatorPosition(currentRef, INDICATOR_HEIGHT));
            (_currentRef_scrollIntoView = currentRef.scrollIntoView) === null || _currentRef_scrollIntoView === void 0 ? void 0 : _currentRef_scrollIntoView.call(currentRef, {
                block: 'nearest',
                behavior: 'smooth'
            });
        }
    }, [
        currentStepIndex
    ]);
    const handleStepClick = (step, state)=>{
        if (state !== StepState.Disabled) {
            onStepClick(step);
        }
    };
    return /*#__PURE__*/ _jsxs(Column, {
        width: 288,
        padding: 40,
        gap: 32,
        className: styles.sidebarRoot,
        testId: SidebarTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(Row, {
                children: vendorLogoUrl ? /*#__PURE__*/ _jsx("img", {
                    src: vendorLogoUrl,
                    alt: relationship.vendor.name,
                    className: styles.vendorLogo,
                    "data-testid": SidebarTestIds.VendorLogo()
                }) : /*#__PURE__*/ _jsx(Typography, {
                    className: styles.vendorName,
                    variant: "h5",
                    testId: SidebarTestIds.VendorName(),
                    children: relationship.vendor.name
                })
            }),
            /*#__PURE__*/ _jsxs(Column, {
                className: styles.stepsList,
                testId: SidebarTestIds.StepsList(),
                children: [
                    /*#__PURE__*/ _jsx(motion.div, {
                        className: styles.indicator,
                        initial: false,
                        animate: {
                            top: indicatorStyle.top,
                            height: indicatorStyle.height
                        },
                        transition: {
                            type: 'spring',
                            stiffness: 500,
                            damping: 35
                        }
                    }),
                    reachableSteps.map((step, index)=>{
                        const state = getStepState(step, currentStep, visitedSteps);
                        const isSelected = state === StepState.Selected;
                        const isDisabled = state === StepState.Disabled;
                        const isEnabled = state === StepState.Enabled;
                        return /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            weight: isSelected ? 'normal' : 'light',
                            skin: isDisabled ? 'disabled' : isSelected ? 'primary' : 'standard',
                            ref: (el)=>stepRefs.current[index] = el,
                            className: cn(styles.stepItem, {
                                [styles.enabled]: isEnabled,
                                [styles.selectedStep]: isSelected
                            }),
                            onClick: ()=>handleStepClick(step, state),
                            testId: SidebarTestIds.StepItem(step),
                            children: getStepName(step, relationship, stepsTexts)
                        }, step);
                    })
                ]
            })
        ]
    });
};
